import "@fontsource/roboto/300.css"
import "@fontsource/roboto/400.css"
import "@fontsource/roboto/500.css"
import "@fontsource/roboto/700.css"
import "react-lazy-load-image-component/src/effects/blur.css"
import "./App.css"
import "./index.css"
import "./router.css"

import React, { Suspense } from "react"

import ReactDOM from "react-dom/client"

import { GluestackUIProvider } from "@gluestack-ui/themed"
import { config as gluestackConfig } from "@gluestack-ui/config"
import { Box } from "@mui/material"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

import { AuthProvider } from "./auth"
import { LocationProvider } from "./components/LocationService"
import { RaffleProvider } from "./contexts/RaffleContext"
import { Provider } from "./MuiProvider"
import { LoadingProvider } from "react-ui-components/components/LoadingService"
import { PopupProvider } from "react-ui-components/components/PopupService"
import { FirebaseProvider } from "react-ui-components/components/contexts/FirebaseContext"
import reportWebVitals from "./reportWebVitals"
import { AppRoutes } from "./routes"
import { SidebarProvider } from "./SidebarProvider"
import { auth as firebaseAuthInstance } from "./firebaseAuth"
import { db as firebaseDb } from "./firebaseFirestore"
import { storage as firebaseStorageInstance } from "./firebaseStorage"

import { BrowserRouter } from "react-router-dom"

import ScrollToAnchor from "./ScrollToAnchor"
import { AppProvider } from "./components/stores/UserContext"
import { useUserStore } from "./components/stores/userStore"
import { ErrorProvider } from "./providers/ErrorProvider"
import { ErrorBoundary } from "./components/system/ErrorBoundary"
import { getAppPopupConfig } from "./utils/popupStyles"
import { sanitizeMuiColorSchemeStorageFromWindow } from "./utils/muiColorSchemeStorage"

const ErrorList = React.lazy(() =>
  import("./components/system/ErrorList").then((module) => ({ default: module.ErrorList })),
)

function PopupConfigProvider({ children }: { children: React.ReactNode }) {
  const { mode } = useUserStore()
  const popupConfig = React.useMemo(() => getAppPopupConfig(mode), [mode])
  return (
    <PopupProvider popupConfig={popupConfig}>
      {children as unknown as Parameters<typeof PopupProvider>[0]["children"]}
    </PopupProvider>
  )
}

sanitizeMuiColorSchemeStorageFromWindow()

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement)

// Paginated list data (raffles, sponsors, etc.) is fetched via useInfiniteQuery. Refetch-on-focus
// is off since Firestore's own bounded onSnapshot listeners already keep first-page data live.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

root.render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <FirebaseProvider auth={firebaseAuthInstance} db={firebaseDb} storage={firebaseStorageInstance}>
        <ErrorProvider>
          <SidebarProvider>
            <LoadingProvider>
              <AppProvider>
                <LocationProvider>
                  <AuthProvider partial>
                    <GluestackUIProvider config={gluestackConfig}>
                      <Box id="__t" sx={{ width: "100vw" }}>
                        <Provider>
                          <RaffleProvider>
                            <ErrorBoundary>
                              <PopupConfigProvider>
                                <ScrollToAnchor />
                                <Suspense fallback={null}>
                                  <ErrorList />
                                </Suspense>
                                <AppRoutes />
                              </PopupConfigProvider>
                            </ErrorBoundary>
                          </RaffleProvider>
                        </Provider>
                      </Box>
                    </GluestackUIProvider>
                  </AuthProvider>
                </LocationProvider>
              </AppProvider>
            </LoadingProvider>
          </SidebarProvider>
        </ErrorProvider>
      </FirebaseProvider>
    </QueryClientProvider>
  </BrowserRouter>
)

reportWebVitals()

// Defer messaging service worker registration so it does not block initial paint
if (typeof window !== "undefined" && "serviceWorker" in navigator && window.isSecureContext) {
  const scheduleMessagingSwRegistration =
    (window as any).requestIdleCallback ||
    ((cb: () => void) => {
      window.setTimeout(cb, 1500)
    })

  scheduleMessagingSwRegistration(() => {
    import("./firebaseMessaging")
      .then(({ getMessagingServiceWorkerRegistration }) => getMessagingServiceWorkerRegistration())
      .catch((error) => {
        console.warn("Service Worker registration deferred/skipped:", error)
      })
  })
}
